import React, { useContext, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ActionButtonHandle,
  AutocompleteValue,
  DetailContext,
  FormAutocomplete,
  FormContext,
  FormDictionaryField,
  UserContext,
  useScrollableSource,
} from '@eas/common-web';
import { useProposerDistinctions } from '@modules/dict-proposer-distinction/dict-proposer-distinction-api';
import { useProposerTypes } from '@modules/dict-proposer-type/dict-proposer-type-api';
import { CONTACTS_SORT, useContacts } from '@modules/subject/subject-api';
import {
  useContactColumns,
  useContactFiltersFields,
} from '@modules/subject/subject-columns';
import { SubjectToolbar } from '@modules/subject/subject-toolbar';
import {
  ContactAutocompleteItem,
  createRowStyle,
} from '@composite/contact/conact-utils';
import {
  autocompleteLabelMapper,
  dictionaryFieldLabelMapper,
} from '@components/form/misc/label-mappers';
import { Contact, EsFile, Subject } from '@models';
import { EsFileState, EvidenceApiUrl, EvidenceBrowserUrl, Role } from '@enums';
import type { ProposerFieldPermissions } from './proposer-field';

interface FormFieldsProps {
  esFile?: EsFile;
  permissions?: ProposerFieldPermissions;
}

export function FormFields({ esFile, permissions }: FormFieldsProps = {}) {
  const { setFieldValue, getFieldValues } = useContext(FormContext);
  const { source } = useContext(DetailContext);
  const { hasPermission } = useContext(UserContext);
  const detailEsFile = esFile ?? source.data;
  const updateClosed = detailEsFile?.state === EsFileState.CLOSED;

  const { contactColumns } = useContactColumns({
    labelPrefix: 'Adresát',
  });
  const { contactFiltersFields } = useContactFiltersFields({
    labelPrefix: 'Adresát',
  });

  const createRef = useRef<ActionButtonHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const contacts = useContacts();
  const contactSource = useScrollableSource({
    url: `${EvidenceApiUrl.SUBJECT_CONTACTS}/list`,
  });

  const proposerTypes = useProposerTypes();
  const proposerDistinctions = useProposerDistinctions();

  const hasSubjectPermission =
    hasPermission(Role.HEAD_OSV) ||
    hasPermission(Role.LUSTRUM) ||
    hasPermission(Role.INDEX) ||
    hasPermission(Role.SECRETARY) ||
    hasPermission(Role.SOLVER) ||
    hasPermission(Role.ANONYMIZATION) ||
    hasPermission(Role.ANALYST) ||
    hasPermission(Role.PUBLICATION);

  return (
    <>
      <SubjectToolbar
        createRef={createRef}
        editRef={editRef}
        fieldName="contact.subject"
        participantMode={true}
        preselectContactId={getFieldValues()?.contact?.id}
        onResultValue={(subject: Subject) => {
          setFieldValue('contact', subject.selectedContact);
        }}
      />
      <FormDictionaryField
        name="contact"
        required={true}
        label={
          <FormattedMessage
            id="ES__PROPOSERS__FIELD_LABEL__SUBJECT"
            defaultMessage="Adresát"
          />
        }
        filters={contactFiltersFields}
        columns={contactColumns}
        autoFocus={true}
        autocompleteSource={contacts}
        dialogSource={contactSource}
        labelMapper={dictionaryFieldLabelMapper}
        maxRows={9}
        dialogWidth={1000}
        onCreate={() => createRef.current?.executeAction()}
        onEdit={() => editRef.current?.executeAction()}
        dialogSorts={CONTACTS_SORT}
        createRowStyle={createRowStyle}
        AutocompleteItemComponent={ContactAutocompleteItem}
        disabled={updateClosed || permissions?.contact === false}
        linkUrl={
          hasSubjectPermission
            ? (value: AutocompleteValue<Contact>) =>
                `${EvidenceBrowserUrl.SUBJECTS}/${value.single?.subject?.id}`
            : undefined
        }
      />
      <FormAutocomplete
        name="proposerType"
        label={
          <FormattedMessage
            id="ES__PROPOSERS__FIELD_LABEL__PROPOSER_TYPE"
            defaultMessage="Typ navrhovatele"
          />
        }
        labelMapper={autocompleteLabelMapper}
        source={proposerTypes}
        disabled={permissions?.type === false}
      />
      <FormAutocomplete
        name="proposerDistinction"
        label={
          <FormattedMessage
            id="ES__PROPOSERS__FIELD_LABEL__PROPOSER_DISTINCTION"
            defaultMessage="Rozlišení navrhovatele"
          />
        }
        labelMapper={autocompleteLabelMapper}
        source={proposerDistinctions}
        disabled={permissions?.distinction === false}
      />
    </>
  );
}
